import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { searchAppCategory, deleteAppCategory } from '../../../constant/Constant';
import { Pencil, Trash2, Plus, Image } from 'lucide-react';
import toast from 'react-hot-toast';

const CategoryMaster = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    setLoading(true);
    const json = {
      page: 1,
      limit: 100000,
      sortBy: 'sortOrder',
      sortOrder: 'asc'
    };
    HitApi(json, searchAppCategory)
      .then((res) => {
        if (res?.data?.docs) {
          setCategories(res.data.docs);
        } else if (res?.data && Array.isArray(res.data)) {
          setCategories(res.data);
        }
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
        toast.error('Failed to fetch categories');
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!deleteModal.id) return;
    HitApi({ _id: deleteModal.id }, deleteAppCategory)
      .then((res) => {
        if (res?.statusCode === 200 || res?.success) {
          toast.success('Category deleted successfully');
          fetchCategories();
        } else {
          toast.error(res?.message || 'Failed to delete category');
        }
      })
      .catch(() => toast.error('Failed to delete category'))
      .finally(() => setDeleteModal({ open: false, id: null }));
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Categories</h1>
          <p className="text-sm text-gray-500 mt-1">Manage categories for the mobile app</p>
        </div>
        <button
          onClick={() => navigate('/app-control/category/add')}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      )}

      {/* Empty State */}
      {!loading && categories.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No categories found</p>
          <button
            onClick={() => navigate('/app-control/category/add')}
            className="mt-4 text-sm text-black underline"
          >
            Create your first category
          </button>
        </div>
      )}

      {/* Category Grid */}
      {!loading && categories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <div
              key={cat._id}
              className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden group hover:shadow-md transition-shadow"
            >
              {/* Header Banner */}
              {cat.headerBanner ? (
                <img
                  src={cat.headerBanner}
                  alt={cat.name}
                  className="w-full h-32 object-cover"
                />
              ) : (
                <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-300" />
                </div>
              )}

              <div className="p-4">
                {/* Name & Icon */}
                <div className="flex items-center gap-3 mb-3">
                  {cat.icon && (
                    <img src={cat.icon} alt="" className="w-8 h-8 rounded-lg object-cover" />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${cat.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex gap-4 text-xs text-gray-500 mb-3">
                  <span>{cat.promotionalBanner?.length || 0} Promo Banners</span>
                  <span>{cat.animatedBanner?.length || 0} Animated Banners</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigate(`/app-control/category/edit/${cat._id}`)}
                    className="flex items-center gap-1 text-sm px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteModal({ open: true, id: cat._id })}
                    className="flex items-center gap-1 text-sm px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Category</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this category? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryMaster;
